import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Share2, Loader2, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { NassauResultCard } from "@/components/round-summary/NassauResultCard";
import { SkinsResultCard } from "@/components/round-summary/SkinsResultCard";
import { generateRoundIntelligence, type RIInput, RoundIntelligenceSection } from "@/lib/roundIntelligence";
import { computeBestStrokeUtilization, BestStrokeUtilizationSection } from "@/lib/strokeUtilization";
import { getFrontHoles, getBackHoles } from "@/lib/gameUtils";

type LegResult = { winner: string; margin: number; amount: number };

type PlayerSummary = {
  participantId: string;
  displayName: string;
  team: string;
  netAmount: number;
  pressWins: number;
  pressLosses: number;
  strokesReceived: number;
  withStrokeHoles: number;
  withStrokeWins: number;
  withStrokeTies: number;
  withStrokeLosses: number;
  withStrokeWinPct: number | null;
  straightUpHoles: number;
  straightUpWins: number;
  straightUpTies: number;
  straightUpLosses: number;
  straightUpWinPct: number | null;
  personalHoleWins: number | null;
  performanceLabel: string | null;
};

type HighlightHole = {
  holeNumber: number;
  description: string;
  pressInfo: string | null;
};

type SummaryResponse = {
  matchMeta: {
    roundId: string;
    date: string | null;
    course: string;
    matchFormat: string;
    gameMode: string;
    gameType: "Nassau" | "Skins";
    payoutType: string | null;
    stakes: { front: number; back: number; overall: number };
    holesPlayed: number;
    isFinalized: boolean;
  };
  participants: Array<{
    participantId: string;
    displayName: string;
    team: string;
    handicap: number | null;
    isWinner: boolean;
    netAmount: number;
    isCurrentUser: boolean;
  }>;
  nassauResult: { front: LegResult; back: LegResult; overall: LegResult } | null;
  presses: Array<{
    pressNumber: number;
    type: "OnDemand" | "TwoDownAuto";
    startingHole: number;
    initiatedBy: string | null;
    winner: string | null;
    amount: number;
    betType?: string;
  }>;
  holeByHole: Array<{
    holeNumber: number;
    scores: Array<{
      participantId: string;
      displayName: string;
      grossScore: number | null;
      netScore: number | null;
      receivesStroke: boolean;
    }>;
    frontStatus: string | null;
    backStatus: string | null;
    overallStatus: string | null;
  }>;
  holes: Array<{ number: number; par: number; yardage: number; strokeIndex: number }>;
  startingHole: 1 | 10;
  strokeImpact: {
    currentUserDisplayName: string;
    withStrokes: { holeNumbers: number[]; wins: number; ties: number; losses: number; winPct: number; tiePct: number; lossPct: number } | null;
    straightUp: { holeNumbers: number[]; wins: number; ties: number; losses: number; winPct: number; tiePct: number; lossPct: number } | null;
  } | null;
  highlightHoles: HighlightHole[];
  playerSummaries: PlayerSummary[];
  teamHoleResults: { teamHolesWon: number; teamHolesLost: number; teamHolesHalved: number } | null;
  divergenceCallout: string | null;
  rivalryContext: { record: string | null };
  viewer: { isGroupMember: boolean; currentParticipantId: string | null };
};

function formatMoney(n: number): string {
  const abs = Math.abs(n);
  return `$${abs % 1 === 0 ? abs.toFixed(0) : abs.toFixed(2)}`;
}

function formatDate(d: string | null): string {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function prettyGameMode(gm: string): string {
  if (gm === "TwoDownAuto") return "Two Down Auto";
  if (gm === "OnDemandPress") return "On-Demand Press";
  return gm;
}

function prettyPayout(p: string | null): { label: string; classes: string } | null {
  if (!p || p === "Full") return null;
  if (p === "QuickPay") return { label: "Quick Pay", classes: "bg-amber-50 text-amber-700 border-amber-200" };
  if (p === "HolidayPay") return { label: "Holiday Pay", classes: "bg-blue-50 text-blue-700 border-blue-200" };
  return { label: p, classes: "bg-gray-50 text-gray-700 border-gray-200" };
}

function Skeleton() {
  return (
    <div className="min-h-screen bg-gray-50 px-4 py-4 max-w-2xl mx-auto" data-testid="summary-loading">
      <div className="animate-pulse space-y-3">
        <div className="h-6 w-2/3 bg-gray-200 rounded" />
        <div className="h-4 w-1/2 bg-gray-200 rounded" />
        <div className="flex gap-2 mt-2">
          <div className="h-6 w-16 bg-gray-200 rounded-full" />
          <div className="h-6 w-20 bg-gray-200 rounded-full" />
          <div className="h-6 w-24 bg-gray-200 rounded-full" />
        </div>
        <div className="h-32 bg-gray-200 rounded-xl mt-2" />
        <div className="h-40 bg-gray-200 rounded-xl" />
        <div className="h-64 bg-gray-200 rounded-xl" />
      </div>
    </div>
  );
}

function Headline({ data }: { data: SummaryResponse }) {
  const teamA = data.participants.filter((p) => p.team === "A");
  const teamB = data.participants.filter((p) => p.team === "B");
  const teamAName = teamA.map((p) => p.displayName).join(" & ");
  const teamBName = teamB.map((p) => p.displayName).join(" & ");
  const teamAWon = teamA[0]?.netAmount > 0;
  const teamBWon = teamB[0]?.netAmount > 0;

  if (!data.matchMeta.isFinalized || (!teamAWon && !teamBWon)) {
    return (
      <p className="font-black text-xl text-gray-900 leading-tight" data-testid="text-headline">
        {teamAName} vs {teamBName}
      </p>
    );
  }
  const winner = teamAWon ? teamAName : teamBName;
  const loser = teamAWon ? teamBName : teamAName;
  return (
    <p className="font-black text-xl text-gray-900 leading-tight" data-testid="text-headline">
      <span className="text-emerald-700">{winner}</span> def. {loser}
    </p>
  );
}

function NetDollar({ data }: { data: SummaryResponse }) {
  const me = data.participants.find((p) => p.isCurrentUser);
  if (!me) {
    return (
      <p className="text-2xl font-black text-gray-400" data-testid="text-net-amount">
        —
      </p>
    );
  }
  const net = me.netAmount;
  const color = net > 0 ? "text-emerald-600" : net < 0 ? "text-red-500" : "text-gray-400";
  return (
    <p className={`text-2xl font-black ${color}`} data-testid="text-net-amount">
      {net > 0 ? "+" : net < 0 ? "-" : ""}
      {formatMoney(net)}
    </p>
  );
}

function MetaBadges({ data }: { data: SummaryResponse }) {
  const payout = prettyPayout(data.matchMeta.payoutType);
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="meta-badges">
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-gray-100 text-gray-700 border border-gray-200" data-testid="badge-format">
        {data.matchMeta.matchFormat}
      </span>
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid="badge-game-type">
        {data.matchMeta.gameType}
      </span>
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-gray-100 text-gray-700 border border-gray-200" data-testid="badge-press-style">
        {prettyGameMode(data.matchMeta.gameMode)}
      </span>
      {payout && (
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border ${payout.classes}`} data-testid="badge-payout">
          {payout.label}
        </span>
      )}
    </div>
  );
}

// ─── Task 6: Player Performance Section (TOP of highlights) ─────────────────
function PlayerPerfRow({ p, isCurrent, showMoney }: { p: PlayerSummary; isCurrent: boolean; showMoney: boolean }) {
  const netColor = p.netAmount > 0 ? "text-emerald-600" : p.netAmount < 0 ? "text-red-500" : "text-gray-400";
  return (
    <div data-testid={`player-perf-${p.participantId}`}>
      <div className="flex items-baseline justify-between mb-1">
        <p className={`text-sm font-bold truncate ${isCurrent ? "text-emerald-800" : "text-gray-900"}`}>
          {p.displayName}
          {isCurrent && <span className="ml-1 text-xs font-normal text-emerald-500">You</span>}
        </p>
        {showMoney && (
          <p className={`text-base font-black ml-2 shrink-0 ${netColor}`} data-testid={`net-amount-${p.participantId}`}>
            {p.netAmount > 0 ? "+" : p.netAmount < 0 ? "-" : ""}
            {formatMoney(p.netAmount)}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1">
        {p.personalHoleWins !== null && (
          <span className="text-[12px] text-gray-700 font-medium" data-testid={`holes-won-${p.participantId}`}>
            <span className="font-black text-gray-900">{p.personalHoleWins}</span> hole{p.personalHoleWins === 1 ? "" : "s"} won
          </span>
        )}
        {p.withStrokeHoles > 0 ? (
          <span className="text-[12px] text-gray-500" data-testid={`stroke-conv-${p.participantId}`}>
            <span className="font-bold text-gray-700">{p.withStrokeWinPct !== null ? `${p.withStrokeWinPct.toFixed(1)}%` : "—"}</span> stroke conv.
          </span>
        ) : (
          p.personalHoleWins !== null && (
            <span className="text-[12px] text-gray-400" data-testid={`stroke-conv-${p.participantId}`}>
              No stroke holes
            </span>
          )
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="text-[11px] text-gray-400" data-testid={`press-record-${p.participantId}`}>
          Presses{" "}
          <span className="font-bold text-gray-600">{p.pressWins}-{p.pressLosses}</span>
        </span>
        {p.performanceLabel && (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200"
            data-testid={`perf-label-${p.participantId}`}
          >
            {p.performanceLabel}
          </span>
        )}
      </div>
    </div>
  );
}

function PlayerPerformanceSection({ data }: { data: SummaryResponse }) {
  if (data.playerSummaries.length === 0) return null;
  const me = data.participants.find((p) => p.isCurrentUser);
  const showMoney = data.viewer.isGroupMember;
  const is2v2 = data.matchMeta.matchFormat === "2v2";

  if (is2v2) {
    const teamA = data.playerSummaries.filter((p) => p.team === "A");
    const teamB = data.playerSummaries.filter((p) => p.team === "B");

    const TeamGroup = ({ players, teamLabel }: { players: PlayerSummary[]; teamLabel: string }) => {
      const teamNet = players[0]?.netAmount ?? 0;
      const netColor = teamNet > 0 ? "text-emerald-600" : teamNet < 0 ? "text-red-500" : "text-gray-400";
      const names = players.map((p) => p.displayName).join(" / ");
      const hasCurrentUser = players.some((p) => me && me.participantId === p.participantId);

      return (
        <div className="py-3" data-testid={`team-perf-${teamLabel}`}>
          {/* Team header: names + shared payout */}
          <div className="flex items-baseline justify-between mb-2">
            <p className={`text-sm font-bold ${hasCurrentUser ? "text-emerald-800" : "text-gray-900"}`}>
              {names}
              {hasCurrentUser && <span className="ml-1 text-xs font-normal text-emerald-500">You</span>}
            </p>
            {showMoney && (
              <p className={`text-base font-black ml-2 shrink-0 ${netColor}`} data-testid={`team-net-${teamLabel}`}>
                {teamNet > 0 ? "+" : teamNet < 0 ? "-" : ""}
                {formatMoney(teamNet)}
              </p>
            )}
          </div>
          {/* Per-player detail rows (compact — no individual net amount) */}
          <div className="space-y-2 pl-1 border-l-2 border-gray-100">
            {players.map((p) => {
              const isCurrent = !!(me && me.participantId === p.participantId);
              return (
                <div key={p.participantId} data-testid={`player-perf-${p.participantId}`}>
                  <p className={`text-xs font-semibold mb-0.5 ${isCurrent ? "text-emerald-700" : "text-gray-700"}`}>
                    {p.displayName}{isCurrent && <span className="ml-1 font-normal text-emerald-500">You</span>}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    {p.personalHoleWins !== null && (
                      <span className="text-[11px] text-gray-600" data-testid={`holes-won-${p.participantId}`}>
                        <span className="font-bold text-gray-800">{p.personalHoleWins}</span> holes won
                      </span>
                    )}
                    {p.withStrokeHoles > 0 ? (
                      <span className="text-[11px] text-gray-500" data-testid={`stroke-conv-${p.participantId}`}>
                        <span className="font-bold text-gray-700">{p.withStrokeWinPct !== null ? `${p.withStrokeWinPct.toFixed(1)}%` : "—"}</span> stroke conv.
                      </span>
                    ) : (
                      p.personalHoleWins !== null && (
                        <span className="text-[11px] text-gray-400" data-testid={`stroke-conv-${p.participantId}`}>No stroke holes</span>
                      )
                    )}
                    <span className="text-[11px] text-gray-400" data-testid={`press-record-${p.participantId}`}>
                      Presses <span className="font-bold text-gray-600">{p.pressWins}-{p.pressLosses}</span>
                    </span>
                    {p.performanceLabel && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200" data-testid={`perf-label-${p.participantId}`}>
                        {p.performanceLabel}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <section className="bg-white rounded-xl border border-gray-100 p-4 space-y-3" data-testid="section-player-performance">
        <h2 className="text-sm font-black text-gray-900">Player Performance</h2>
        <div className="divide-y divide-gray-100">
          <TeamGroup players={teamA} teamLabel="A" />
          <TeamGroup players={teamB} teamLabel="B" />
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-xl border border-gray-100 p-4 space-y-3" data-testid="section-player-performance">
      <h2 className="text-sm font-black text-gray-900">Player Performance</h2>
      <div className="divide-y divide-gray-100">
        {data.playerSummaries.map((p) => {
          const isCurrent = !!(me && me.participantId === p.participantId);
          return (
            <div key={p.participantId} className="py-3">
              <PlayerPerfRow p={p} isCurrent={isCurrent} showMoney={showMoney} />
            </div>
          );
        })}
      </div>
    </section>
  );
}


// ─── Divergence Callout (Task 3) ────────────────────────────────────────────
function DivergenceCallout({ callout }: { callout: string | null }) {
  if (!callout) return null;
  return (
    <section
      className="bg-amber-50 rounded-xl border border-amber-200 px-4 py-3 flex items-start gap-2"
      data-testid="section-divergence"
    >
      <Zap className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
      <p className="text-xs text-amber-800 font-medium leading-relaxed">{callout}</p>
    </section>
  );
}

// ─── Stroke Impact bar chart (current user only, backward compat) ────────────
function StrokeImpactBar({
  label,
  data,
  testIdPrefix,
}: {
  label: string;
  data: { wins: number; ties: number; losses: number; winPct: number; tiePct: number; lossPct: number };
  testIdPrefix: string;
}) {
  const seg = (pct: number) => `${Math.max(pct, pct > 0 ? 8 : 0)}%`;
  return (
    <div data-testid={testIdPrefix}>
      <p className="text-sm font-bold text-gray-700 mb-1.5">{label}</p>
      <div className="flex h-7 rounded-md overflow-hidden bg-gray-100">
        {data.winPct > 0 && (
          <div
            className="bg-emerald-500 flex items-center justify-center text-xs font-bold text-white"
            style={{ width: seg(data.winPct) }}
            data-testid={`${testIdPrefix}-win`}
          >
            {data.winPct >= 12 ? `Win ${data.winPct.toFixed(1)}%` : `${data.winPct.toFixed(1)}%`}
          </div>
        )}
        {data.tiePct > 0 && (
          <div
            className="bg-gray-400 flex items-center justify-center text-xs font-bold text-white"
            style={{ width: seg(data.tiePct) }}
            data-testid={`${testIdPrefix}-tie`}
          >
            {data.tiePct >= 12 ? `Tie ${data.tiePct.toFixed(1)}%` : `${data.tiePct.toFixed(1)}%`}
          </div>
        )}
        {data.lossPct > 0 && (
          <div
            className="bg-red-500 flex items-center justify-center text-xs font-bold text-white"
            style={{ width: seg(data.lossPct) }}
            data-testid={`${testIdPrefix}-loss`}
          >
            {data.lossPct >= 12 ? `Loss ${data.lossPct.toFixed(1)}%` : `${data.lossPct.toFixed(1)}%`}
          </div>
        )}
      </div>
    </div>
  );
}

function StrokeImpactSection({ data }: { data: SummaryResponse }) {
  if (!data.strokeImpact) return null;
  const { withStrokes, straightUp } = data.strokeImpact;
  if (!withStrokes && !straightUp) return null;
  if (
    withStrokes && withStrokes.holeNumbers.length === 0 &&
    (!straightUp || straightUp.holeNumbers.length === 0) &&
    withStrokes.wins + withStrokes.ties + withStrokes.losses === 0
  ) return null;

  const hasWithStrokes = withStrokes && (withStrokes.wins + withStrokes.ties + withStrokes.losses > 0);
  const hasStraightUp = straightUp && (straightUp.wins + straightUp.ties + straightUp.losses > 0);
  if (!hasWithStrokes && !hasStraightUp) return null;

  return (
    <section className="bg-white rounded-xl border border-gray-100 p-4 space-y-3" data-testid="section-stroke-impact">
      <h2 className="text-sm font-black text-gray-900 flex items-baseline gap-1.5 flex-wrap">
        Stroke Breakdown
        <span className="text-sm font-bold text-gray-700">— {data.strokeImpact.currentUserDisplayName}</span>
      </h2>
      {hasWithStrokes && (
        <StrokeImpactBar label="With Strokes" data={withStrokes!} testIdPrefix="stroke-impact-with" />
      )}
      {hasStraightUp && (
        <StrokeImpactBar label="Straight Up" data={straightUp!} testIdPrefix="stroke-impact-straight" />
      )}
    </section>
  );
}

// ─── Highlight Holes (Task 4) — default view, up to 5 curated holes ──────────
function HighlightHolesSection({ data }: { data: SummaryResponse }) {
  if (!data.highlightHoles || data.highlightHoles.length === 0) return null;
  return (
    <section className="bg-white rounded-xl border border-gray-100 p-4 space-y-2" data-testid="section-highlight-holes">
      <h2 className="text-sm font-black text-gray-900">Match Highlights</h2>
      <ul className="divide-y divide-gray-100">
        {data.highlightHoles.map((hl) => (
          <li
            key={`hl-${hl.holeNumber}`}
            className="flex items-start justify-between py-2 gap-3"
            data-testid={`highlight-hole-${hl.holeNumber}`}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-[11px] font-black text-gray-700">
                {hl.holeNumber}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{hl.description}</p>
                {hl.pressInfo && (
                  <p className="text-[11px] text-amber-600 font-medium flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    {hl.pressInfo}
                  </p>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ─── Final Scorecard (horizontal, matches ResultsScreen layout) ───────────────
function HistoricalScorecard({ data }: { data: SummaryResponse }) {
  // Derived from the round's actual starting hole so a back-nine (10) start
  // renders holes 10-18 first (Out) then 1-9 (In) — matching the physical
  // play order, not always hole 1 first. See getFrontHoles/getBackHoles.
  const frontNums = getFrontHoles(data.startingHole);
  const backNums = getBackHoles(data.startingHole);

  const holeMap = new Map(data.holeByHole.map((h) => [h.holeNumber, h]));
  const holeInfoMap = new Map(data.holes.map((h) => [h.number, h]));

  const allParticipants = [...data.participants].sort((a, b) =>
    a.team === b.team ? 0 : a.team === "A" ? -1 : 1
  );

  function scoreDisplay(participantId: string, holeNum: number): string {
    const holeRow = holeMap.get(holeNum);
    if (!holeRow) return "-";
    const s = holeRow.scores.find((sc) => sc.participantId === participantId);
    if (!s || s.grossScore === null) return "-";
    if (s.receivesStroke && s.netScore !== null && s.netScore !== s.grossScore) {
      return `${s.grossScore}/${s.netScore}`;
    }
    return `${s.grossScore}`;
  }

  function totalDisplay(participantId: string, holeNums: number[]): string {
    let grossSum = 0;
    let netSum = 0;
    let hasAny = false;
    let anyStroke = false;
    for (const h of holeNums) {
      const holeRow = holeMap.get(h);
      if (!holeRow) continue;
      const s = holeRow.scores.find((sc) => sc.participantId === participantId);
      if (!s || s.grossScore === null) continue;
      hasAny = true;
      grossSum += s.grossScore;
      netSum += s.netScore ?? s.grossScore;
      if (s.receivesStroke && s.netScore !== null && s.netScore !== s.grossScore) anyStroke = true;
    }
    if (!hasAny) return "-";
    if (anyStroke && netSum !== grossSum) return `${grossSum}/${netSum}`;
    return `${grossSum}`;
  }

  const frontPar = frontNums.reduce((s, h) => s + (holeInfoMap.get(h)?.par ?? 0), 0);
  const backPar = backNums.reduce((s, h) => s + (holeInfoMap.get(h)?.par ?? 0), 0);
  const totalPar = frontPar + backPar;
  const frontYds = frontNums.reduce((s, h) => s + (holeInfoMap.get(h)?.yardage ?? 0), 0);
  const backYds = backNums.reduce((s, h) => s + (holeInfoMap.get(h)?.yardage ?? 0), 0);
  const totalYds = frontYds + backYds;
  const hasYardage = totalYds > 0;
  const hasStrokeIndex = data.holes.some((h) => h.strokeIndex > 0);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-testid="section-final-scorecard">
      <div className="bg-gray-800 text-white px-4 py-2">
        <h2 className="font-bold text-sm uppercase tracking-wide">Final Scorecard</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-100 border-b">
              <th className="px-2 py-1 text-left font-semibold sticky left-0 bg-gray-100 z-10">Hole</th>
              {frontNums.map((h) => (
                <th key={h} data-testid={`scorecard-header-${h}`} className="px-1 py-1 text-center font-semibold min-w-[28px]">{h}</th>
              ))}
              <th data-testid="scorecard-header-out" className="px-2 py-1 text-center font-semibold bg-gray-200">Out</th>
              {backNums.map((h) => (
                <th key={h} data-testid={`scorecard-header-${h}`} className="px-1 py-1 text-center font-semibold min-w-[28px]">{h}</th>
              ))}
              <th data-testid="scorecard-header-in" className="px-2 py-1 text-center font-semibold bg-gray-200">In</th>
              <th className="px-2 py-1 text-center font-semibold bg-gray-300">Tot</th>
            </tr>
            {hasYardage && (
              <tr className="bg-blue-50 border-b text-xs">
                <td className="px-2 py-0.5 text-left text-blue-600 sticky left-0 bg-blue-50 z-10">Yds</td>
                {frontNums.map((h) => (
                  <td key={h} className="px-1 py-0.5 text-center text-blue-600">
                    {holeInfoMap.get(h)?.yardage || "-"}
                  </td>
                ))}
                <td className="px-2 py-0.5 text-center text-blue-600 bg-blue-100">{frontYds || "-"}</td>
                {backNums.map((h) => (
                  <td key={h} className="px-1 py-0.5 text-center text-blue-600">
                    {holeInfoMap.get(h)?.yardage || "-"}
                  </td>
                ))}
                <td className="px-2 py-0.5 text-center text-blue-600 bg-blue-100">{backYds || "-"}</td>
                <td className="px-2 py-0.5 text-center text-blue-600 bg-blue-200">{totalYds || "-"}</td>
              </tr>
            )}
            {hasStrokeIndex && (
              <tr className="bg-amber-50 border-b text-xs">
                <td className="px-2 py-0.5 text-left text-amber-700 sticky left-0 bg-amber-50 z-10">Hdcp</td>
                {frontNums.map((h) => (
                  <td key={h} className="px-1 py-0.5 text-center text-amber-700">
                    {holeInfoMap.get(h)?.strokeIndex || "-"}
                  </td>
                ))}
                <td className="px-2 py-0.5 text-center text-amber-700 bg-amber-100"></td>
                {backNums.map((h) => (
                  <td key={h} className="px-1 py-0.5 text-center text-amber-700">
                    {holeInfoMap.get(h)?.strokeIndex || "-"}
                  </td>
                ))}
                <td className="px-2 py-0.5 text-center text-amber-700 bg-amber-100"></td>
                <td className="px-2 py-0.5 text-center text-amber-700 bg-amber-200"></td>
              </tr>
            )}
            <tr className="bg-gray-50 border-b">
              <td className="px-2 py-1 text-left text-gray-500 sticky left-0 bg-gray-50 z-10">Par</td>
              {frontNums.map((h) => (
                <td key={h} className="px-1 py-1 text-center text-gray-500">
                  {holeInfoMap.get(h)?.par ?? "-"}
                </td>
              ))}
              <td className="px-2 py-1 text-center text-gray-500 bg-gray-100">{frontPar || "-"}</td>
              {backNums.map((h) => (
                <td key={h} className="px-1 py-1 text-center text-gray-500">
                  {holeInfoMap.get(h)?.par ?? "-"}
                </td>
              ))}
              <td className="px-2 py-1 text-center text-gray-500 bg-gray-100">{backPar || "-"}</td>
              <td className="px-2 py-1 text-center text-gray-500 bg-gray-200">{totalPar || "-"}</td>
            </tr>
          </thead>
          <tbody>
            {allParticipants.map((player) => (
              <tr key={player.participantId} className="border-b hover:bg-gray-50">
                <td className="px-2 py-1 font-bold text-gray-900 sticky left-0 bg-white z-10 whitespace-nowrap">
                  {player.displayName}
                </td>
                {frontNums.map((h) => {
                  const display = scoreDisplay(player.participantId, h);
                  const isStrokeHole = display.includes("/");
                  return (
                    <td key={h} className="px-1 py-1 text-center" data-testid={`scorecard-${player.participantId}-${h}`}>
                      {display === "-" ? (
                        <span className="text-gray-300">-</span>
                      ) : isStrokeHole ? (
                        <span className="font-semibold text-emerald-700">{display}</span>
                      ) : (
                        <span className="font-semibold text-gray-900">{display}</span>
                      )}
                    </td>
                  );
                })}
                <td className="px-2 py-1 text-center font-semibold bg-gray-100">
                  {totalDisplay(player.participantId, frontNums)}
                </td>
                {backNums.map((h) => {
                  const display = scoreDisplay(player.participantId, h);
                  const isStrokeHole = display.includes("/");
                  return (
                    <td key={h} className="px-1 py-1 text-center" data-testid={`scorecard-${player.participantId}-${h}`}>
                      {display === "-" ? (
                        <span className="text-gray-300">-</span>
                      ) : isStrokeHole ? (
                        <span className="font-semibold text-emerald-700">{display}</span>
                      ) : (
                        <span className="font-semibold text-gray-900">{display}</span>
                      )}
                    </td>
                  );
                })}
                <td className="px-2 py-1 text-center font-semibold bg-gray-100">
                  {totalDisplay(player.participantId, backNums)}
                </td>
                <td className="px-2 py-1 text-center font-bold bg-gray-200">
                  {totalDisplay(player.participantId, [...frontNums, ...backNums])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Match Stats Section ─────────────────────────────────────────────────────
function cellStyle(val: "W" | "L" | "Tied"): string {
  if (val === "W") return "text-emerald-600 font-black";
  if (val === "L") return "text-red-500 font-black";
  return "text-gray-400 font-semibold";
}

function SkinsMatchStats({ data }: { data: SummaryResponse }) {
  const { playerSummaries, matchMeta, rivalryContext } = data;
  if (playerSummaries.length === 0) return null;

  const totalHoles = matchMeta.holesPlayed;
  const maxSkins = Math.max(...playerSummaries.map((p) => p.personalHoleWins ?? 0));
  const hasAnyData = playerSummaries.some((p) => p.personalHoleWins !== null);

  if (!hasAnyData) return null;

  return (
    <section className="bg-white rounded-xl border border-gray-100 p-4" data-testid="section-match-stats">
      <h2 className="text-sm font-black text-gray-900 mb-3">Match Stats</h2>
      <div className="w-full">
        <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 mb-1.5">
          <div />
          <div className="text-[11px] font-bold text-gray-500 text-center min-w-[56px]" data-testid="stats-header-skins">
            Skins Won
          </div>
          <div className="text-[11px] font-bold text-gray-500 text-center min-w-[44px]" data-testid="stats-header-holes">
            / Holes
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {playerSummaries.map((p) => {
            const skins = p.personalHoleWins ?? 0;
            const isLeader = maxSkins > 0 && skins === maxSkins;
            return (
              <div
                key={p.participantId}
                className="grid grid-cols-[1fr_auto_auto] gap-x-3 py-2"
                data-testid={`stats-skins-row-${p.participantId}`}
              >
                <span className={`text-xs truncate ${isLeader ? "font-bold text-emerald-700" : "text-gray-700"}`}>
                  {p.displayName}
                </span>
                <span
                  className={`text-xs text-center min-w-[56px] font-bold ${isLeader ? "text-emerald-600" : "text-gray-600"}`}
                  data-testid={`skins-won-${p.participantId}`}
                >
                  {skins}
                </span>
                <span className="text-xs text-center min-w-[44px] text-gray-400" data-testid={`holes-played-${p.participantId}`}>
                  {totalHoles}
                </span>
              </div>
            );
          })}
        </div>
        {rivalryContext.record && (
          <div className="pt-2 border-t border-gray-100 mt-1" data-testid="stats-row-h2h">
            <span className="text-[11px] text-gray-400 font-medium">H2H · </span>
            <span className="text-[11px] text-gray-600">{rivalryContext.record}</span>
          </div>
        )}
      </div>
    </section>
  );
}

function MatchStatsSection({ data }: { data: SummaryResponse }) {
  if (data.matchMeta.gameType === "Skins") {
    return <SkinsMatchStats data={data} />;
  }

  const teamA = data.participants.filter((p) => p.team === "A");
  const teamB = data.participants.filter((p) => p.team === "B");

  // Unsupported format fallback (more than 2 players per side = 3v3+)
  if (teamA.length > 2 || teamB.length > 2) {
    return (
      <section className="bg-white rounded-xl border border-gray-100 p-4" data-testid="section-match-stats">
        <h2 className="text-sm font-black text-gray-900 mb-2">Match Stats</h2>
        <p className="text-xs text-gray-500">Detailed match stats are currently available for 1v1 and 2v2 formats only</p>
      </section>
    );
  }

  if (teamA.length === 0 || teamB.length === 0) return null;

  const is2v2 = data.matchMeta.matchFormat === "2v2";
  const teamALabel = teamA.map((p) => p.displayName).join(" & ") || "Team A";
  const teamBLabel = teamB.map((p) => p.displayName).join(" & ") || "Team B";

  // Front / Back — derived from nassauResult, winner string matched against team labels
  function getLegCells(leg: LegResult): { a: "W" | "L" | "Tied"; b: "W" | "L" | "Tied" } | null {
    if (leg.margin === 0) return { a: "Tied", b: "Tied" };
    if (leg.winner === teamALabel) return { a: "W", b: "L" };
    if (leg.winner === teamBLabel) return { a: "L", b: "W" };
    return null; // winner doesn't map to either column — omit
  }

  const nr = data.nassauResult;
  const frontCells = nr ? getLegCells(nr.front) : null;
  const backCells = nr ? getLegCells(nr.back) : null;

  // Presses — omit entire row if any resolved winner maps to neither label
  let pressCells: { a: number; b: number } | null = null;
  if (data.presses.length > 0) {
    const resolved = data.presses.filter((pr) => pr.winner !== null);
    const ambiguous = resolved.some((pr) => pr.winner !== teamALabel && pr.winner !== teamBLabel);
    if (!ambiguous) {
      pressCells = {
        a: resolved.filter((pr) => pr.winner === teamALabel).length,
        b: resolved.filter((pr) => pr.winner === teamBLabel).length,
      };
    }
  }

  // Holes — 1v1 uses personalHoleWins from playerSummaries; 2v2 uses teamHoleResults
  let holesCells: { a: string; b: string } | null = null;
  if (is2v2) {
    if (data.teamHoleResults !== null) {
      holesCells = {
        a: `${data.teamHoleResults.teamHolesWon} won`,
        b: `${data.teamHoleResults.teamHolesLost} won`,
      };
    }
  } else {
    const sA = data.playerSummaries.find((p) => p.team === "A");
    const sB = data.playerSummaries.find((p) => p.team === "B");
    if (sA?.personalHoleWins !== null && sA?.personalHoleWins !== undefined &&
        sB?.personalHoleWins !== null && sB?.personalHoleWins !== undefined) {
      holesCells = {
        a: `${sA.personalHoleWins} won`,
        b: `${sB.personalHoleWins} won`,
      };
    }
  }

  // H2H — only when record is non-null
  const h2h = data.rivalryContext.record ?? null;

  const hasRows = frontCells || backCells || pressCells !== null || holesCells || h2h;
  if (!hasRows) return null;

  return (
    <section className="bg-white rounded-xl border border-gray-100 p-4" data-testid="section-match-stats">
      <h2 className="text-sm font-black text-gray-900 mb-3">Match Stats</h2>
      <div className="w-full">
        {/* Column headers */}
        <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 mb-1.5">
          <div />
          <div className="text-[11px] font-bold text-gray-500 text-center truncate max-w-[110px]" data-testid="stats-header-a">
            {teamALabel}
          </div>
          <div className="text-[11px] font-bold text-gray-500 text-center truncate max-w-[110px]" data-testid="stats-header-b">
            {teamBLabel}
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {frontCells && (
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 py-2" data-testid="stats-row-front">
              <span className="text-xs text-gray-500">Front</span>
              <span className={`text-xs text-center min-w-[40px] ${cellStyle(frontCells.a)}`}>{frontCells.a}</span>
              <span className={`text-xs text-center min-w-[40px] ${cellStyle(frontCells.b)}`}>{frontCells.b}</span>
            </div>
          )}
          {backCells && (
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 py-2" data-testid="stats-row-back">
              <span className="text-xs text-gray-500">Back</span>
              <span className={`text-xs text-center min-w-[40px] ${cellStyle(backCells.a)}`}>{backCells.a}</span>
              <span className={`text-xs text-center min-w-[40px] ${cellStyle(backCells.b)}`}>{backCells.b}</span>
            </div>
          )}
          {pressCells !== null && (
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 py-2" data-testid="stats-row-presses">
              <span className="text-xs text-gray-500">Presses</span>
              <span className={`text-xs text-center min-w-[40px] font-bold ${pressCells.a > pressCells.b ? "text-emerald-600" : pressCells.a < pressCells.b ? "text-red-500" : "text-gray-500"}`}>
                {pressCells.a}
              </span>
              <span className={`text-xs text-center min-w-[40px] font-bold ${pressCells.b > pressCells.a ? "text-emerald-600" : pressCells.b < pressCells.a ? "text-red-500" : "text-gray-500"}`}>
                {pressCells.b}
              </span>
            </div>
          )}
          {holesCells && (
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 py-2" data-testid="stats-row-holes">
              <span className="text-xs text-gray-500">Holes</span>
              <span className="text-xs text-center min-w-[40px] font-semibold text-gray-700">{holesCells.a}</span>
              <span className="text-xs text-center min-w-[40px] font-semibold text-gray-700">{holesCells.b}</span>
            </div>
          )}
          {h2h && (
            <div className="py-2" data-testid="stats-row-h2h">
              <span className="text-[11px] text-gray-400 font-medium">H2H · </span>
              <span className="text-[11px] text-gray-600">{h2h}</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function RoundSummary() {
  const { roundId } = useParams<{ roundId: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [sharing, setSharing] = useState(false);

  const { data, isLoading, error } = useQuery<SummaryResponse>({
    queryKey: ["/api/rounds", roundId, "summary"],
    queryFn: async () => {
      const res = await fetch(`/api/rounds/${roundId}/summary`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    enabled: !!roundId,
  });

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ url, title: "Match Summary" });
      } else {
        await navigator.clipboard.writeText(url);
        toast({ description: "Link copied", duration: 2000 });
      }
    } catch {
      try {
        await navigator.clipboard.writeText(url);
        toast({ description: "Link copied", duration: 2000 });
      } catch {
        toast({ description: "Couldn't share link", duration: 2000, variant: "destructive" });
      }
    } finally {
      setSharing(false);
    }
  };

  if (isLoading) return <Skeleton />;
  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6" data-testid="summary-error">
        <div className="text-center space-y-4 max-w-sm">
          <h2 className="text-lg font-bold text-gray-900">Can't Load Match</h2>
          <p className="text-sm text-gray-600">This round may have been removed or isn't available.</p>
          <Button onClick={() => setLocation("/ledger")} variant="outline" data-testid="button-back-error">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Ledger
          </Button>
        </div>
      </div>
    );
  }

  // ─── Round Intelligence ──────────────────────────────────────────────────
  const riTeamA = data.participants.filter(p => p.team === 'A');
  const riTeamB = data.participants.filter(p => p.team === 'B');
  const riTeamALabel = riTeamA.map(p => p.displayName).join(' & ');
  const riTeamBLabel = riTeamB.map(p => p.displayName).join(' & ');
  const riStrokesFor = (participantId: string) =>
    data.playerSummaries.find(s => s.participantId === participantId)?.strokesReceived ?? 0;
  const riInput: RIInput = {
    teams: [
      { players: riTeamA.map(p => ({ id: p.participantId, name: p.displayName, strokesReceived: riStrokesFor(p.participantId) })) },
      { players: riTeamB.map(p => ({ id: p.participantId, name: p.displayName, strokesReceived: riStrokesFor(p.participantId) })) },
    ] as [{ players: { id: string; name: string; strokesReceived: number }[] }, { players: { id: string; name: string; strokesReceived: number }[] }],
    scores: data.holeByHole.flatMap(h =>
      h.scores.map(s => ({
        playerId: s.participantId,
        holeNumber: h.holeNumber,
        grossScore: s.grossScore,
        netScore: s.netScore,
        receivesStroke: s.receivesStroke,
      }))
    ),
    holes: data.holeByHole.map(h => ({ number: h.holeNumber, par: null })),
    presses: data.presses.map(p => ({ startingHole: p.startingHole, stackCount: 1 })),
    matchFormat: data.matchMeta.matchFormat as '1v1' | '2v2',
    startingHole: data.holeByHole[0]?.holeNumber ?? 1,
    stakes: data.matchMeta.stakes,
    totalAmountForA: riTeamA.some(p => p.isWinner) ? 1 : riTeamB.some(p => p.isWinner) ? -1 : 0,
    nassauAmountForA: data.nassauResult
      ? (() => {
          const nr = data.nassauResult!;
          const legAmt = (leg: { winner: string; margin: number; amount: number }) =>
            leg.margin === 0 ? 0 : leg.winner === riTeamALabel ? leg.amount : -leg.amount;
          return legAmt(nr.front) + legAmt(nr.back) + legAmt(nr.overall);
        })()
      : null,
    pressAmountForA: data.presses.reduce((sum, p) => {
      if (p.winner === null) return sum;
      if (p.winner === riTeamALabel) return sum + p.amount;
      if (p.winner === riTeamBLabel) return sum - p.amount;
      return sum;
    }, 0),
  };
  const riCards = generateRoundIntelligence(riInput, 5);

  const bsuRanked = computeBestStrokeUtilization(
    data.playerSummaries.map(p => ({
      name: p.displayName,
      strokesReceived: p.strokesReceived,
      withStrokeWins: p.withStrokeWins,
      withStrokeTies: p.withStrokeTies,
      withStrokeLosses: p.withStrokeLosses,
    }))
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      <main className="px-4 pb-3 safe-area-top max-w-2xl mx-auto space-y-3">
        {/* SECTION 1: HEADER */}
        <header className="flex items-start gap-2" data-testid="summary-header">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (window.history.length > 1) {
                window.history.back();
              } else {
                setLocation("/ledger");
              }
            }}
            className="-ml-2 mt-0.5 shrink-0"
            data-testid="button-back-summary"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <Headline data={data} />
            <p className="text-xs text-gray-500 mt-0.5" data-testid="text-subline">
              {data.matchMeta.course}
              {data.matchMeta.date && ` · ${formatDate(data.matchMeta.date)}`}
            </p>
          </div>
          <div className="text-right shrink-0">
            <NetDollar data={data} />
          </div>
        </header>

        {/* SECTION 2: META BADGES */}
        <MetaBadges data={data} />

        {/* SECTION 2.5: FINAL SCORECARD */}
        <HistoricalScorecard data={data} />

        {/* SECTION 3: PLAYER PERFORMANCE */}
        <PlayerPerformanceSection data={data} />

        {/* SECTION 4: DIVERGENCE CALLOUT */}
        <DivergenceCallout callout={data.divergenceCallout} />

        {/* SECTION 4.5: BEST STROKE UTILIZATION */}
        <BestStrokeUtilizationSection ranked={bsuRanked} />

        {/* SECTION 4.6: RIVALRY CONTEXT */}
        {data.rivalryContext.record && (
          <section
            className="bg-gray-100 rounded-xl border border-gray-200 p-3 text-center"
            data-testid="section-rivalry"
          >
            <p className="text-xs text-gray-600">{data.rivalryContext.record}</p>
          </section>
        )}

        {/* SECTION 4.7: STROKE BREAKDOWN */}
        <StrokeImpactSection data={data} />

        {/* SECTION 4.8: ROUND INTELLIGENCE */}
        <RoundIntelligenceSection cards={riCards} />

        {/* SECTION 5: GAME-MODE-AWARE RESULT CARD */}
        {data.matchMeta.gameType === "Nassau" && data.nassauResult && (
          <NassauResultCard
            data={data.nassauResult}
            presses={data.presses}
            hideMoney={!data.viewer.isGroupMember}
          />
        )}
        {data.matchMeta.gameType === "Skins" && <SkinsResultCard />}

        {/* SECTION 7: HIGHLIGHT HOLES — curated, up to 5 (Task 4) */}
        <HighlightHolesSection data={data} />

        {/* SECTION 9: MATCH STATS */}
        <MatchStatsSection data={data} />
      </main>

      {/* SHARE BUTTON (fixed) */}
      <div
        className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-gray-100 px-4 py-3"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}
        data-testid="share-bar"
      >
        <div className="max-w-2xl mx-auto">
          <Button
            onClick={handleShare}
            disabled={sharing}
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-bold"
            data-testid="button-share-match"
          >
            {sharing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Share2 className="w-4 h-4 mr-2" />
            )}
            Share This Match
          </Button>
        </div>
      </div>
    </div>
  );
}
